interface AuthorizedFetchOptions extends RequestInit {
  tokenKey?: string;
  skipAuth?: boolean;
  onAuthError?: (status: number) => void;
}

interface AuthorizedFetchResponse<T = unknown> {
  data: T | null;
  status: number;
  ok: boolean;
  error?: string;
}

const getAccessToken = (tokenKey: string): string | null => {
  try {
    const item = localStorage.getItem(tokenKey);
    if (!item) {
      return null;
    }
    const parsed = JSON.parse(item);
    if (typeof parsed === 'string') {
      return parsed;
    }
    let accessToken = parsed?.accessToken || parsed?.access_token;

    if (accessToken && typeof accessToken === 'object') {
      accessToken = accessToken.accessToken || accessToken.access_token;
    }
    return accessToken || null;
  } catch (error) {
    return null;
  }
};

const handleAuthError = (status: number, onAuthError?: (status: number) => void) => {
  if (status === 401 || status === 403) {
    if (onAuthError) {
      onAuthError(status);
    }
  }
};

export const authorizedFetch = async <T = unknown>(
  url: string,
  options: AuthorizedFetchOptions = {}
): Promise<AuthorizedFetchResponse<T>> => {
  const { tokenKey = 'okta-token-storage', skipAuth = false, onAuthError, ...fetchOptions } = options;

  const headers: HeadersInit = {
    ...fetchOptions.headers,
  };

  if (!skipAuth) {
    const token = getAccessToken(tokenKey);
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }
  
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    handleAuthError(response.status, onAuthError);


    let data: T | null = null;
    if (response.status !== 204) { // No Content
      const text = await response.text();
      try {
        data = text ? JSON.parse(text) : null;
      } catch (parseError) {
        data = text as unknown as T;
      }
    }

    return {
      data,
      status: response.status,
      ok: response.ok,
      error: !response.ok ? response.statusText : undefined,
    };
  } catch (error) {
    return {
      data: null,
      status: 0,
      ok: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
};
